import { t, type TranslationKey } from '../i18n';
import { $, h, link, pad, richText, svg } from '../ui/dom';
import { icons } from '../ui/icons';

const CHAPTERS = [1, 2, 3, 4] as const;

let index = 0;
let cleanup: (() => void) | null = null;
let keepChapter = false;

// Locale switch re-mounts the page: stay on the same chapter instead of jumping to the start.
document.addEventListener('i18n:changed', () => {
  keepChapter = true;
});

function go(to: number): void {
  const track = $('history-track');
  if (!track) return;
  const max = CHAPTERS.length - 1;
  const next = Math.max(0, Math.min(max, to));
  if (next === index && track.querySelector('.slide.is-active')) return;
  index = next;

  // Old slide leaves to the left, new one enters from the right (and vice versa going back).
  track.querySelectorAll<HTMLElement>('.slide').forEach((slide, i) => {
    slide.classList.toggle('is-active', i === index);
    slide.classList.toggle('is-past', i < index);
    slide.classList.toggle('is-future', i > index);
    slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
    slide.inert = i !== index;
  });
  document.querySelectorAll<HTMLElement>('#history-dots button').forEach((d, i) => d.classList.toggle('active', i === index));
  const counter = $('history-counter');
  if (counter) counter.textContent = `${pad(index + 1)} / ${pad(CHAPTERS.length)}`;
  ($('history-prev') as HTMLButtonElement | null)?.toggleAttribute('disabled', index === 0);
  ($('history-next') as HTMLButtonElement | null)?.toggleAttribute('disabled', index === max);
  $('history-stage')?.classList.toggle('is-last', index === max);
}

function buildSlides(): void {
  const track = $('history-track');
  const dots = $('history-dots');
  if (!track || !dots) return;

  track.replaceChildren(
    ...CHAPTERS.map((n, i) => {
      const isLast = i === CHAPTERS.length - 1;
      return h(
        'article',
        'slide is-future',
        h('span', 'slide-bignum', pad(n)),
        h(
          'div',
          'slide-inner',
          h('span', { class: 'mono accent slide-kicker', text: `${t('history.chapter')} ${pad(n)} / ${pad(CHAPTERS.length)}` }),
          h('h2', { class: 'slide-title', text: t(`history.s${n}.title` as TranslationKey) }),
          richText(t(`history.s${n}.text` as TranslationKey), 'rich slide-text'),
          isLast
            ? h(
                'div',
                'slide-actions',
                link('/join', 'btn btn-primary', t('history.join'), svg(icons.arrowRight)),
                h('button', { class: 'btn btn-ghost', attrs: { type: 'button' }, text: t('history.restart'), on: { click: () => go(0) } }),
              )
            : null,
        ),
      );
    }),
  );

  dots.replaceChildren(
    ...CHAPTERS.map((n, i) =>
      h(
        'button',
        { attrs: { type: 'button', 'aria-label': `${t('history.chapter')} ${n}` }, on: { click: () => go(i) } },
        h('span', 'dot-num mono', pad(n)),
        h('span', { class: 'dot-label', text: t(`history.s${n}.title` as TranslationKey) }),
      ),
    ),
  );
}

export function mountHistory(params: URLSearchParams): void {
  cleanup?.();
  const stage = $('history-stage');
  if (!stage) return;

  buildSlides();
  const start = Number(params.get('ch'));
  const from = Number.isInteger(start) && start >= 1 && start <= CHAPTERS.length ? start - 1 : keepChapter ? Math.max(0, index) : 0;
  keepChapter = false;
  index = -1;
  // Let the initial off-screen state paint first so the first chapter slides in.
  window.setTimeout(() => go(from), 40);

  const onStageClick = (e: MouseEvent): void => {
    if ((e.target as Element).closest('a, button, input')) return;
    if (window.getSelection()?.toString()) return; // allow selecting text
    go(index + 1);
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'ArrowRight' || e.key === 'PageDown' || (e.key === ' ' && document.activeElement === stage)) {
      e.preventDefault();
      go(index + 1);
    } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
      e.preventDefault();
      go(index - 1);
    }
  };
  let touchX: number | null = null;
  const onTouchStart = (e: TouchEvent): void => {
    touchX = e.touches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: TouchEvent): void => {
    const x = e.changedTouches[0]?.clientX;
    if (touchX === null || x === undefined) return;
    const dx = x - touchX;
    touchX = null;
    if (Math.abs(dx) > 50) go(index + (dx < 0 ? 1 : -1));
  };
  const prev = (): void => go(index - 1);
  const next = (): void => go(index + 1);

  stage.addEventListener('click', onStageClick);
  stage.addEventListener('touchstart', onTouchStart, { passive: true });
  stage.addEventListener('touchend', onTouchEnd);
  document.addEventListener('keydown', onKey);
  $('history-prev')?.addEventListener('click', prev);
  $('history-next')?.addEventListener('click', next);

  cleanup = () => {
    stage.removeEventListener('click', onStageClick);
    stage.removeEventListener('touchstart', onTouchStart);
    stage.removeEventListener('touchend', onTouchEnd);
    document.removeEventListener('keydown', onKey);
    $('history-prev')?.removeEventListener('click', prev);
    $('history-next')?.removeEventListener('click', next);
  };
}

export function unmountHistory(): void {
  cleanup?.();
  cleanup = null;
}
